import { useState } from "react";
import { RequirePermission } from "@components/common/require-permission";
import { CompanyTypeKind } from "@custom-types/base-types";
import { WorkspaceTypeDropdown } from "@pages/workspace-profile/workspace-type-dropdown";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { isValidEmail } from "@pages/create-project/create-project-helper";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { NavbarEvents } from "@utils/track-event/track-event-list";
import { useAppNavigation } from "@router/use-app-navigation";
import { MINIMUM_COMPANY_NAME_LENGTH } from "@utils/ui-utils";
import { Button, Dialog, useToast, Text } from "@flat-ui";
import { RichTextField } from "@components/common/rich-text-field";

/** Time window to consider a newly created company for fetching */
const COMPANY_CREATION_WINDOW_MS = 60000;

const initialFormState: {
  workspaceName: string;
  workspaceAdminEmail: string;
  workspaceType: CompanyTypeKind;
} = {
  workspaceName: "",
  workspaceAdminEmail: "",
  workspaceType: "unspecified",
};

/** Component handling the workspace creation */
export function CreateWorkspace(): React.JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isCreatePlanDialogOpen, setIsCreatePlanDialogOpen] =
    useState<boolean>(false);
  const [workspaceName, setWorkspaceName] = useState<string>(
    initialFormState.workspaceName
  );
  const [workspaceAdminEmail, setWorkspaceAdminEmail] = useState<string>(
    initialFormState.workspaceAdminEmail
  );
  const [workspaceType, setWorkspaceType] = useState<CompanyTypeKind>(
    initialFormState.workspaceType
  );
  const [workspaceId, setWorkspaceId] = useState<string>("");

  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorHandler();
  const { openToast } = useToast();

  const { navigateToPlanCreation } = useAppNavigation();

  function handleDialogClose(): void {
    setIsDialogOpen(false);
    setIsCreatePlanDialogOpen(false);
    setWorkspaceId("");

    // Reset form to initial state
    setWorkspaceName(initialFormState.workspaceName);
    setWorkspaceAdminEmail(initialFormState.workspaceAdminEmail);
    setWorkspaceType(initialFormState.workspaceType);
  }

  function onFetchPlan(): void {
    if (workspaceId) {
      navigateToPlanCreation({
        subjectType: "COMPANY",
        subjectId: workspaceId,
      });
      handleDialogClose();
      trackEvent({
        name: NavbarEvents.createPlanForNewlyCreatedWorkspace,
        props: {
          companyId: workspaceId,
        },
      });
    } else {
      trackEvent({
        name: NavbarEvents.fetchNewlyCreatedWorkspacePlan,
      });
      void fetchNewlyCreatedCompany();
    }
  }

  async function fetchNewlyCreatedCompany(): Promise<void> {
    if (!isCreatePlanDialogOpen) {
      return;
    }

    try {
      const companies = await faroCoreApiClient.admin.searchCompanies({
        search: workspaceName,
      });

      // Names are unique, so we can safely filter by name. But we also need to check for creation date
      // as maybe the company name existed before and we don't want to pick that one.
      const foundCompany = companies.find(
        (company) =>
          company.name === workspaceName &&
          company.createdAt >= Date.now() - COMPANY_CREATION_WINDOW_MS
      );

      if (foundCompany) {
        setWorkspaceId(foundCompany.id);
      }
    } catch (error) {
      handleErrorWithToast({
        id: `create-workspace-fetch-${Date.now().toString()}`,
        title: "Could not fetch the newly created workspace",
        error,
      });
    }
  }

  async function onCompanyCreate(): Promise<void> {
    trackEvent({
      name: NavbarEvents.createCompany,
      props: {
        type: workspaceType,
        workspaceNameLength: workspaceName.length,
        companyId: "",
        projectId: "",
      },
    });
    try {
      await faroCoreApiClient.admin.createCompany({
        companyName: workspaceName,
        ceoMail: workspaceAdminEmail,
        type: workspaceType,
      });
      openToast({
        title: "Workspace creation request submitted.",
        message:
          "You'll receive an email shortly with a link to the workspace profile once the workspace is created.",
      });
      setIsCreatePlanDialogOpen(true);
    } catch (error) {
      handleErrorWithToast({
        id: `create-company-${Date.now().toString()}`,
        title: "Could not create workspace",
        error,
      });
    } finally {
      setIsDialogOpen(false);
    }
  }

  function checkValidity(): {
    isCompanyNameValid: boolean;
    isAdminEmailValid: boolean;
  } {
    return {
      isCompanyNameValid:
        workspaceName.trim().length >= MINIMUM_COMPANY_NAME_LENGTH,
      isAdminEmailValid: isValidEmail(workspaceAdminEmail),
    };
  }

  return (
    <>
      <RequirePermission permission="workspace.create">
        <Button onClick={() => setIsDialogOpen(true)}>Create Workspace</Button>
      </RequirePermission>

      <Dialog
        open={isDialogOpen}
        title="Create workspace"
        onCancel={handleDialogClose}
        onConfirm={onCompanyCreate}
        isConfirmDisabled={Object.values(checkValidity()).some(
          (isValid) => !isValid
        )}
      >
        <RichTextField
          label="Workspace Name"
          text={workspaceName}
          onChange={setWorkspaceName}
          helpTooltip="This is the name that the customers will use to access their workspace. It can be changed in the future"
          validation={{
            isValid: checkValidity().isCompanyNameValid,
            message: "Minimum 3 characters",
          }}
        />

        <RichTextField
          label="Workspace Admin Email"
          text={workspaceAdminEmail}
          onChange={setWorkspaceAdminEmail}
          helpTooltip="This is the email of the workspace admin. An invitation email will be sent to this email address"
          validation={{
            isValid: checkValidity().isAdminEmailValid,
            message: "Please enter a valid email address",
          }}
        />

        <WorkspaceTypeDropdown
          selectedType={workspaceType}
          onTypeSelect={setWorkspaceType}
        />
      </Dialog>

      <Dialog
        open={isCreatePlanDialogOpen}
        title="Create plan for the new workspace"
        onConfirm={onFetchPlan}
        onClose={handleDialogClose}
        confirmText={workspaceId ? "Create Plan" : "Fetch Again"}
      >
        <Text variant="bodyL">
          Workspace creation typically takes a few moments. Click "Fetch Again"
          to search for your newly created workspace. Once found, you can
          proceed to create a plan for it. If the workspace doesn't appear after
          multiple attempts, please close this dialog and check your email for
          further information.
        </Text>
      </Dialog>
    </>
  );
}
